/** @jsx jsx */
import { jsx, useTheme } from '@emotion/react';

import { useEffect } from 'react';
import Media from 'react-media';

import { camelCase, startCase } from 'lodash';

import { useRouter } from 'js/lib/useRouter';
import { isAuthenticatedUser } from 'js/lib/user';

import { Grid, LoadingSection } from '@coursera/cds-core';

import { useBrowseQuerySkillsByName } from 'bundles/browse-common/hooks/useBrowseQuerySkillsByName';
import OtherTopics from 'bundles/browse-common/legacy/components/Domain/OtherTopics';
import { isBrowseSkillsEnabled, previewBrowseSkillsV2 } from 'bundles/browse-common/utils/epicUtils';
import { isNotExcludedSkill, seoQueriesToSkills } from 'bundles/browse-common/utils/skill-utils';
import DomainTopSkills from 'bundles/browse/components/Domain/DomainTopSkills';
import { getSkillsForSubDomain } from 'bundles/browse/components/Domain/utils/subdomainUtils';
import BrowseFAQs from 'bundles/browse/components/Faq/BrowseFAQs';
import sessionStorage from 'bundles/common/utils/sessionStorageEx';
import TrackedDiv from 'bundles/page/components/TrackedDiv';
import useUserAgent from 'bundles/page/hooks/useUserAgent';
import SearchBar from 'bundles/search-common/components/searchbar/SearchBar';
import { TABLET_SCREEN_WIDTH } from 'bundles/search-common/constants';
import { isSearchTheme } from 'bundles/search-common/searchTheme';
import { getNameFromDomainOrSubdomainId } from 'bundles/search-common/utils/SearchUtils';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import {
  getShouldShowBrowseInSERP,
  getShowNewAutocomplete,
  getShowRegistrationGate,
} from 'bundles/search-common/utils/experimentUtils';
import SearchPageRoot from 'bundles/search-v2/components/SearchPageRoot';
import EntityPageCanonicalTagWrapper from 'bundles/seo-entity-page/components/EntityPageCanonicalTagWrapper';
import EntityPageHeader from 'bundles/seo-entity-page/components/EntityPageHeader';
import SEOConfigurationBottomContent from 'bundles/seo-entity-page/components/SEOConfigurationBottomContent';
import { SeoSearchProviderWrapper } from 'bundles/seo-entity-page/components/SeoSearchProviderWrapper';
import SkillPage from 'bundles/seo-entity-page/components/SkillPage';
import { getCurrentEntityPageNum } from 'bundles/seo-entity-page/components/utils';
import { QUERY_TO_SUBDOMAIN_SKILL_MAPPING } from 'bundles/seo-entity-page/constants';
import { useSeoPageData } from 'bundles/seo/contexts/SeoPageDataContext';

export const SEOEntityPage = () => {
  const router = useRouter();
  const theme = useTheme();
  const userAgent = useUserAgent();
  const { eqpData } = useSeoPageData();
  const shouldShowBrowseInSERP = getShouldShowBrowseInSERP();

  const domainSkillsHeader = eqpData?.headers?.domainSkillsHeader?.label;
  const searchQuery = router.location.query;
  const { search, pathname } = router.location;
  const hasSearchQuery = !!searchQuery.query;
  const showTopContentBlock = eqpData?.topContentBlock && !shouldShowBrowseInSERP;
  const topContentBlock = showTopContentBlock && eqpData.topContentBlock !== null ? eqpData.topContentBlock : undefined;
  const searchPageRootProps = { isSEOEntityPage: !!searchQuery.query, router };
  const currentPage = getCurrentEntityPageNum(router);

  useEffect(() => {
    // if search query is not in entity list, redirect to /search on client or show 404

    let entityQuery: string;

    // Spaces are usually replaced with `%20` or `+` in encoded urls. However,
    // space characters within the query string of the url are usually encoded as `+`.
    // Therefore, any query values containing a `+` symbol as part of the value for example `C++` must
    // be encoded in order for the symbol to be interpreted as part of the value instead of a space.

    // We are replacing all `+` space representations with `%20` to match the canonical urls and prevent duplicate entity pages
    if (search && /\+/.test(search)) {
      entityQuery = search.replace(/\+/g, '%20');
      const redirectUrl = `${pathname}${entityQuery}`;
      router.replace(redirectUrl);
    }
  }, [router, searchQuery, search, pathname, hasSearchQuery]);

  useEffect(() => {
    const hasSeenEqpRegistrationGate = sessionStorage.getItem('hasSeenEqpRegistrationGate', JSON.parse, false);

    const shouldShowRegistrationGate =
      !isAuthenticatedUser() && !hasSeenEqpRegistrationGate && getShowRegistrationGate() && currentPage >= 3;

    if (shouldShowRegistrationGate) {
      router.replace({
        pathname,
        query: { ...router.location.query, authMode: 'signup' },
      });
      sessionStorage.setItem('hasSeenEqpRegistrationGate', true, JSON.stringify);
    }
  }, [router, currentPage, pathname]);

  const renderRelatedSkills = () => {
    const entityPageMappedSubdomain = QUERY_TO_SUBDOMAIN_SKILL_MAPPING[searchQuery.query];

    if (!entityPageMappedSubdomain) {
      return null;
    }

    const subdomainId = entityPageMappedSubdomain.subdomain;
    const domainId = entityPageMappedSubdomain.domain;

    const skillsList = getSkillsForSubDomain(subdomainId);

    if (!skillsList.length) {
      return null;
    }

    return (
      <DomainTopSkills
        headerLabel={domainSkillsHeader}
        domainName={getNameFromDomainOrSubdomainId(domainId)}
        subDomainName={getNameFromDomainOrSubdomainId(subdomainId)}
        skillsList={skillsList}
        isMobileBrowser={userAgent?.isMobileBrowser ?? false}
        isEntityPage
        entityPageUrl={pathname + search}
      />
    );
  };

  const renderFaqs = () => {
    const titleCaseSearchQuery = startCase(camelCase(searchQuery.query));
    const titleCaseEQPDataFaqTopic = eqpData?.faqSlug ? startCase(camelCase(eqpData?.faqSlug)) : titleCaseSearchQuery;
    const isOnFirstPage = currentPage === 1;
    // We should only render FAQs if there exists a search query term and if you're on page 1
    return (
      isOnFirstPage &&
      hasSearchQuery && (
        <BrowseFAQs isEntityPage appSlug="search" faqTopic={titleCaseEQPDataFaqTopic} faqSlug={eqpData?.faqSlug} />
      )
    );
  };

  const maybeSkillQuery = inferQueryFromParams(searchQuery?.query).trim().toLowerCase();

  const { data: browseSkill, loading: browseSkillLoading } = useBrowseQuerySkillsByName(maybeSkillQuery);
  // Check first if we have a direct match from the API, otherwise try the hardcoded mapping
  const skillSlug = browseSkill?.id || seoQueriesToSkills[maybeSkillQuery];

  if (previewBrowseSkillsV2() && browseSkillLoading) {
    return <LoadingSection />;
  }

  if (!browseSkillLoading && !!skillSlug && isNotExcludedSkill(skillSlug) && isBrowseSkillsEnabled()) {
    return <SkillPage skillSlug={skillSlug} />;
  }

  if (!isSearchTheme(theme)) {
    throw new Error(
      'SEOEntityPage can only be used in the context of a SearchTheme. Wrap this component with a ThemeProvider.'
    );
  }
  const showNewAutocomplete = getShowNewAutocomplete();

  return (
    <TrackedDiv
      className="rc-SEOEntityPage"
      trackingName="seo_entity_page"
      trackClicks={false}
      requireFullyVisible={false}
      withVisibilityTracking={true}
      atMostOnce
      data={{ searchQuery }}
      css={[theme.searchPageContainer, theme.entityPageContainer, theme.seoEntityNested]}
    >
      <EntityPageCanonicalTagWrapper query={searchQuery} pathname={pathname} />
      {!showNewAutocomplete && (
        <Media query={{ maxWidth: TABLET_SCREEN_WIDTH }} defaultMatches={userAgent?.isMobileBrowser}>
          {(matches) => {
            return (
              matches && (
                <div className="mobile-search-page-bar">
                  <SearchBar />
                </div>
              )
            );
          }}
        </Media>
      )}
      <EntityPageHeader userAgent={userAgent} contentBlock={topContentBlock} />
      <SeoSearchProviderWrapper isSEOEntityPage={!!searchQuery}>
        <SearchPageRoot {...searchPageRootProps} />
      </SeoSearchProviderWrapper>
      <Grid container css={theme.entityPageBody} spacing={8}>
        <SEOConfigurationBottomContent />
        <Grid item container className="internal-linking-wrapper" xs={12} spacing={8}>
          <Grid item xs={12} className="skills-to-learn-section">
            {renderRelatedSkills()}
          </Grid>
          <Grid item xs={12} className="faq-and-topics-container cds">
            {renderFaqs()}
            <OtherTopics />
          </Grid>
        </Grid>
      </Grid>
    </TrackedDiv>
  );
};

export default SEOEntityPage;
